import { motion } from "framer-motion";
import { ArrowUpRight, GraduationCap, Award, Users } from "lucide-react";
import { CLINIC } from "@/data/content";

export default function DoctorSpotlight() {
  return (
    <section id="doctor" className="py-20 md:py-32 bg-white relative overflow-hidden" data-testid="doctor">
      <div className="absolute inset-0 -z-10 opacity-40">
        <div className="absolute top-20 left-10 w-72 h-72 rounded-full bg-[hsl(var(--primary-light))] blur-3xl" />
        <div className="absolute bottom-10 right-10 w-96 h-96 rounded-full bg-[hsl(var(--accent)/0.15)] blur-3xl" />
      </div>

      <div className="max-w-[1440px] mx-auto px-5 md:px-12">
        <div className="grid grid-cols-12 gap-8 md:gap-14 items-center">
          {/* Portrait */}
          <div className="col-span-12 md:col-span-5 order-2 md:order-1">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
              className="relative mx-auto max-w-[340px] md:max-w-[420px]"
            >
              <div className="absolute inset-6 -z-10 rounded-full bg-[hsl(var(--primary)/0.16)]" />
              <img
                src="/assets/dr-raksha.jpeg"
                alt="Dr Raksha Shetty — Founder & lead dentist at Calm Dentistry"
                className="w-full aspect-[3/4] object-cover rounded-[140px_140px_20px_20px] md:rounded-[180px_180px_24px_24px]"
                loading="lazy"
              />
              <div className="absolute right-2 md:-right-4 bottom-8 bg-white rounded-2xl shadow-[0_20px_50px_-20px_rgba(46,36,27,0.35)] p-3 md:p-4 flex items-center gap-2.5 md:gap-3">
                <div className="w-10 h-10 rounded-full bg-[hsl(var(--primary))] text-white flex items-center justify-center">
                  <Award size={18} />
                </div>
                <div>
                  <div className="font-display leading-none">B.D.S.</div>
                  <div className="text-[11px] text-[hsl(var(--ink-soft))] mt-1">Advanced Cosmetology</div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Copy */}
          <div className="col-span-12 md:col-span-7 order-1 md:order-2">
            <div className="eyebrow mb-4">Meet your dentist</div>
            <h2 className="font-display text-3xl md:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.02em]">
              {CLINIC.doctor}. <br />
              <span className="text-[hsl(var(--primary))]">Committed to gentle, precise care.</span>
            </h2>

            <div className="mt-8 grid md:grid-cols-2 gap-6 text-[hsl(var(--ink-soft))] leading-relaxed">
              <p>
                Dr Raksha Shetty is a compassionate, calm dentist practising in Andheri East & Powai. She believes dentistry should be gentle, precise and personalised — with unhurried appointments and clear treatment planning.
              </p>
              <p>
                From nervous first-timers to complex smile-design cases, she treats each mouth with meticulous attention and a naturally calming presence. The clinical rigour is matched only by how quiet she keeps the room.
              </p>
            </div>

            <div className="mt-8 md:mt-10 grid grid-cols-3 gap-3 md:gap-4">
              <StatBadge icon={<GraduationCap size={18} />} value="B.D.S." label="Dental Surgery" />
              <StatBadge icon={<Award size={18} />} value="Cosmetology" label="Advanced smile design" />
              <StatBadge icon={<Users size={18} />} value="15+ yrs" label="Andheri · Powai" />
            </div>

            <a href="#book" className="btn-pill btn-solid mt-8 md:mt-10" data-testid="doctor-book-cta">
              Book with Dr Raksha <span className="arrow"><ArrowUpRight size={16} /></span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function StatBadge({ icon, value, label }) {
  return (
    <div className="rounded-2xl border border-[hsl(var(--line))] bg-white p-3 md:p-5">
      <div className="w-9 h-9 md:w-10 md:h-10 rounded-xl bg-[hsl(var(--primary-light))] text-[hsl(var(--primary-dark))] flex items-center justify-center mb-3 md:mb-4">
        {icon}
      </div>
      <div className="font-display text-sm md:text-lg leading-tight text-[hsl(var(--ink))]">{value}</div>
      <div className="text-[11px] md:text-[12px] text-[hsl(var(--ink-soft))] mt-1 md:mt-2 leading-tight">{label}</div>
    </div>
  );
}
